import React, { useState } from 'react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useKubernetesContext } from './KubernetesContext';
import { KubernetesTable } from './tables';
import { KubernetesScheduleManager } from './KubernetesScheduleManager';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { CRDKubernetesConfig } from '@internal/backstage-plugin-compute-resource-discovery-new-common';

// Helper function to group Kubernetes configs by context
const groupKubernetesConfigs = (configs: CRDKubernetesConfig[]) => {
  const grouped: { [key: string]: CRDKubernetesConfig[] } = {};

  configs.forEach(config => {
    const key = config.kubeContext || 'unknown';
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(config);
  });

  return grouped;
};

export const Kubernetes: React.FC<{ entity: Entity }> = ({ entity }) => {
  const { kubernetesConfigs } = useKubernetesContext();
  const groupedConfigs = groupKubernetesConfigs(kubernetesConfigs);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <>
      {Object.entries(groupedConfigs).map(([kubeContext, configs]) => (
        <Box key={kubeContext} sx={{ mb: 4 }}>
          {/* Kubernetes Context Header */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#E8F4FD',
              borderRadius: 1,
              border: '1px solid #326CE5',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ color: '#326CE5', fontWeight: 'bold' }}
              >
                Context: {kubeContext}
              </Typography>
            </Box>
          </Box>

          {/* Tabs for Resources and Schedule Manager */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="kubernetes tabs"
            >
              <Tab label="Resources" />
              <Tab label="Schedule Manager" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {configs.map((config, configIdx) => (
            <Box key={configIdx} sx={{ mb: 2 }}>
              {activeTab === 0 && (
                <KubernetesTable
                  title="Kubernetes Resources"
                  entityRef={stringifyEntityRef(entity)}
                  kubernetesConfig={config}
                />
              )}
              {activeTab === 1 && (
                <KubernetesScheduleManager
                  entityRef={stringifyEntityRef(entity)}
                  kubernetesConfig={config}
                />
              )}
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};
